// src/pages/ServiceDesk/index.js

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { Container, Card, Button } from 'react-bootstrap';

import axios from '../../services/axios';
import Loading from '../../components/Loading';
import Breadcrumbs from '../../components/Breadcrumbs';
import DataTable from '../../components/DataTable';
import ButtonModal from '../../components/ModalConfirmation/ButtonModal';
import { useNotification } from '../../components/Notification/NotificationContext';
import ErrorProcessor from '../../components/ErrorProcessor';

export default function ServiceDesks() {
  const routeName = '/servicedesk';
  const dataName = 'Mesa de Serviço';
  const titleName = 'Mesas de Serviço';
  const navigate = useNavigate();
  const notification = useNotification();

  const [serviceDesks, setServiceDesks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [apiErrors, setApiErrors] = useState({});

  // 1) Definição das colunas: Nome, Clientes, Departamentos, Ações
  const tableColumns = useMemo(
    () => [
      { Header: 'Nome', accessor: 'displayName' },
      { Header: 'Clientes', accessor: 'clientCount', noSearch: true },
      { Header: 'Departamentos', accessor: 'departmentCount', noSearch: true },
      { Header: 'Ações', accessor: 'actions', noSearch: true },
    ],
    []
  );

  // 2) Ao montar, buscar todos os ServiceDesks
  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const response = await axios.get('/servicedesks');
        // response.data deve conter, em cada objeto:
        // { id, displayName, customers: [ ... ], departments: [ ... ], ... }
        const serviceDesksData = response.data.map((sd) => ({
          id: sd.id,
          displayName: sd.displayName,
          clientCount: Array.isArray(sd.customers) ? sd.customers.length : 0,
          departmentCount: Array.isArray(sd.departments)
            ? sd.departments.length
            : 0,
        }));
        setServiceDesks(serviceDesksData);
      } catch (error) {
        console.error('Erro ao carregar mesas de serviço:', error);
        setApiErrors(error);
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, []);

  // 3) Função para confirmar e remover uma mesa de serviço
  const confirmDelete = useCallback(
    async (id) => {
      setIsLoading(true);
      try {
        await axios.delete(`/servicedesks/${id}`);
        notification(`${dataName} removida com sucesso.`, 'success');
        setServiceDesks((prev) => prev.filter((d) => d.id !== id));
      } catch (err) {
        console.error('Erro ao remover mesa de serviço:', err);
        setApiErrors(err);
      } finally {
        setIsLoading(false);
      }
    },
    [notification]
  );

  // 4) Constrói os dados para exibir no DataTable
  const tableData = useMemo(
    () =>
      serviceDesks.map((sd) => {
        // Botões “Visualizar” e “Remover” alinhados à direita
        const actions = (
          <div className="d-flex justify-content-end gap-1">
            <Button
              size="sm"
              variant="primary"
              className="px-2"
              onClick={() => navigate(`${routeName}/${sd.id}/edit`)}
            >
              Visualizar
            </Button>
            <ButtonModal
              onConfirm={() => confirmDelete(sd.id)}
              message={`Confirma remoção da Mesa "${sd.displayName}"?`}
              buttonProps={{
                variant: 'danger',
                size: 'sm',
                className:
                  'd-flex align-items-center justify-content-center px-2',
                title: 'Remover', // tooltip ao passar o mouse
              }}
              textModal={{
                title: `Remover Mesa de Serviço`,
                textConfirm: 'Remover',
              }}
              dropdown={false}
            >
              <FaTrash size={14} color="#fff" aria-label="Remover" />
            </ButtonModal>
          </div>
        );

        return {
          displayName: sd.displayName,
          clientCount: sd.clientCount,
          departmentCount: sd.departmentCount,
          actions,
        };
      }),
    [serviceDesks, navigate, confirmDelete]
  );

  return (
    <Container fluid className="px-lg-4 px-xl-5">
      <Loading isLoading={isLoading} />
      <Breadcrumbs title={titleName} />
      <div className="page-header d-flex justify-content-between align-items-center mb-3">
        <h1 className="page-heading">{titleName}</h1>
        <Link to={`${routeName}/new`}>
          <Button variant="primary" className="text-uppercase">
            <FaPlus /> Cadastrar
          </Button>
        </Link>
      </div>
      <section>
        <Card className="card-table mb-5">
          <ErrorProcessor error={apiErrors} />
          <DataTable
            items={tableData}
            columns={tableColumns}
            defaultPageSize={5}
          />
        </Card>
      </section>
    </Container>
  );
}
